import React, { FC, useCallback, useMemo } from 'react'
import { Tabs, DatePicker } from 'antd'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { useHistory, useLocation } from 'react-router-dom'
import { TAB_KEYS, tabItems } from './utils'
import Financials from '../Overview/Financials'
import Listings from '../Listings/Listings'
import type { FinancialsProps } from '../Overview/Financials'
import type { ListingsProps } from '../Listings/Listings'
import ContactInfo from '../ContactInfo/ContactInfo'
import type { STREntityResponse } from '../types'
import type { YearRange } from '../utils'
import { sumValuesForRange, filterChartDataByRange, buildListingMarkersForRange } from '../utils'

const useDetailsParams = () => {
  const history = useHistory()
  const { search } = useLocation()
  const params = new URLSearchParams(search)

  const activeTab = params.get('tab') || TAB_KEYS.FINANCIALS

  const startYearParam = params.get('startYear')
  const endYearParam = params.get('endYear')
  const yearRange: YearRange =
    startYearParam && endYearParam ? [parseInt(startYearParam, 10), parseInt(endYearParam, 10)] : null

  const dateRange: [Dayjs, Dayjs] | null = yearRange ? [dayjs().year(yearRange[0]), dayjs().year(yearRange[1])] : null

  const replaceParams = useCallback(
    (updater: (p: URLSearchParams) => void) => {
      const next = new URLSearchParams(search)
      updater(next)
      history.replace({ search: next.toString() })
    },
    [history, search],
  )

  const setActiveTab = useCallback(
    (tab: string) =>
      replaceParams(p => {
        if (tab === TAB_KEYS.FINANCIALS) p.delete('tab')
        else p.set('tab', tab)
      }),
    [replaceParams],
  )

  const setDateRange = useCallback(
    (vals: [Dayjs | null, Dayjs | null] | null) =>
      replaceParams(p => {
        if (vals?.[0] && vals?.[1]) {
          p.set('startYear', String(vals[0].year()))
          p.set('endYear', String(vals[1].year()))
        } else {
          p.delete('startYear')
          p.delete('endYear')
        }
      }),
    [replaceParams],
  )

  return { activeTab, setActiveTab, yearRange, dateRange, setDateRange }
}

interface FilterableData {
  totalRevenue: number
  unreportedRevenue: number
  overviewChartData: FinancialsProps['overviewChartData']
  listingStats: ListingsProps['listingStats']
  listingMarkers: ListingsProps['listingMarkers']
  listingRows: ListingsProps['listingRows']
  byYear: STREntityResponse['totals']['byYear'] | undefined
  revenueSources: STREntityResponse['revenueSources']
}

const useFilteredData = (data: FilterableData, yearRange: YearRange) => {
  const {
    totalRevenue,
    unreportedRevenue,
    overviewChartData,
    listingStats,
    listingMarkers,
    listingRows,
    byYear,
    revenueSources,
  } = data

  const filteredTotalRevenue = useMemo(
    () => (yearRange ? sumValuesForRange(byYear?.total ?? byYear?.documented, yearRange) : totalRevenue),
    [yearRange, byYear, totalRevenue],
  )

  const filteredUnreportedRevenue = useMemo(
    () => (yearRange ? sumValuesForRange(byYear?.unReportedByYear, yearRange) : unreportedRevenue),
    [yearRange, byYear, unreportedRevenue],
  )

  const filteredChartData = useMemo(
    () => filterChartDataByRange(overviewChartData, yearRange),
    [overviewChartData, yearRange],
  )

  const filteredListingRows = listingRows

  const filteredListingMarkers = useMemo(
    () => (yearRange ? buildListingMarkersForRange(revenueSources, yearRange) : listingMarkers),
    [yearRange, revenueSources, listingMarkers],
  )

  const filteredListingStats = useMemo(() => {
    if (!yearRange) return listingStats
    return {
      ...listingStats,
      estimatedRevenue: sumValuesForRange(byYear?.estimated, yearRange),
      reportedRevenue: sumValuesForRange(byYear?.reportedByYear, yearRange) || null,
    }
  }, [yearRange, listingStats, byYear])

  return {
    totalRevenue: filteredTotalRevenue,
    unreportedRevenue: filteredUnreportedRevenue,
    overviewChartData: filteredChartData,
    listingRows: filteredListingRows,
    listingMarkers: filteredListingMarkers,
    listingStats: filteredListingStats,
  }
}

type DetailsProps = FinancialsProps &
  ListingsProps & {
    entityId?: string
    byYear: STREntityResponse['totals']['byYear'] | undefined
    revenueSources: STREntityResponse['revenueSources']
    contactInfo: STREntityResponse['contactInfo']
    reportedStatus?: STREntityResponse['entityDetails']['reportedStatus']
  }

const collectEntityYears = (byYear: STREntityResponse['totals']['byYear'] | undefined): number[] => {
  if (!byYear) return []
  const years = new Set<number>()
  Object.values(byYear).forEach(record => {
    if (record && typeof record === 'object') {
      Object.keys(record).forEach(yearKey => {
        const yearNum = parseInt(yearKey, 10)
        if (!Number.isNaN(yearNum)) years.add(yearNum)
      })
    }
  })
  return [...years]
}

const Details: FC<DetailsProps> = props => {
  const { activityIndex, byYear } = props
  const { activeTab, setActiveTab, yearRange, dateRange, setDateRange } = useDetailsParams()
  const filtered = useFilteredData(props, yearRange)

  const entityYears = useMemo(() => collectEntityYears(byYear), [byYear])
  const entityMinYear = entityYears.length ? Math.min(...entityYears) : null
  const entityMaxYear = entityYears.length ? Math.max(...entityYears) : null

  const tabContent: Record<string, React.ReactNode> = {
    [TAB_KEYS.FINANCIALS]: (
      <Financials
        totalRevenue={filtered.totalRevenue}
        unreportedRevenue={filtered.unreportedRevenue}
        overviewChartData={filtered.overviewChartData}
        entityId={props.entityId}
        activityIndex={props.activityIndex}
        byYear={props.byYear}
        reportedStatus={props.reportedStatus}
      />
    ),
    [TAB_KEYS.LISTING]: (
      <Listings
        listingStats={filtered.listingStats}
        listingMarkers={filtered.listingMarkers}
        listingRows={filtered.listingRows}
        activityIndex={activityIndex}
      />
    ),
    [TAB_KEYS.CONTACT_INFO]: <ContactInfo contactInfo={props.contactInfo} />,
  }

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        tabBarExtraContent={
          activeTab === TAB_KEYS.LISTING ? (
            <DatePicker.RangePicker
              picker='year'
              value={dateRange}
              onChange={vals => setDateRange(vals ? [vals[0], vals[1]] : null)}
              minDate={entityMinYear ? dayjs().year(entityMinYear).startOf('year') : undefined}
              maxDate={entityMaxYear ? dayjs().year(entityMaxYear).endOf('year') : undefined}
              allowClear
            />
          ) : null
        }
      />
      {tabContent[activeTab]}
    </div>
  )
}

export default Details
